import { useCallback, useRef } from 'react'

/**
 * Returns a throttled version of the callback that executes at most once per interval.
 * Schedules execution after `delay` ms, ignoring calls while one is pending.
 */
export function useThrottledCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delay: number
): (...args: Args) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const argsRef = useRef<Args | null>(null)
  const callbackRef = useRef(callback)
  callbackRef.current = callback

  return useCallback(
    (...args: Args) => {
      argsRef.current = args

      if (timeoutRef.current) return // Already scheduled

      timeoutRef.current = setTimeout(() => {
        if (argsRef.current) {
          callbackRef.current(...argsRef.current)
        }
        timeoutRef.current = null
      }, delay)
    },
    [delay]
  )
}
